// Inline "Add sub-account" form for a channel's vendor list. Two
// inputs (display name + vendor_id) and a Save button. Posts to
// /admin/integrations/:code/vendors and returns the refreshed channel
// so the parent's vendors[] re-renders without a separate reload.
//
// The form starts collapsed behind an "Add sub-account" button —
// expanding only when the admin actually wants to add one — so the
// vendor list stays tidy when no new vendor is being created.

import { useState } from "react";
import { Loader2, Plus, X, Check } from "lucide-react";
import { useTranslation } from "../../../i18n";
import { apiFetch } from "../../../api/apiFetch";
import type { SalesChannelConfig } from "./types";

interface Props {
    channelCode: string;
    onCreated: (next: SalesChannelConfig) => void;
    disabled?: boolean;
}

// Client-side mirror of the BE VENDOR_KEY_PATTERN. Stops obvious
// typos before the round-trip; the BE re-validates.
const VENDOR_KEY_PATTERN = /^[a-z][a-z0-9_]{0,127}$/;

const AddVendorForm = ({ channelCode, onCreated, disabled = false }: Props) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [displayName, setDisplayName] = useState("");
    const [vendorId, setVendorId] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const reset = () => {
        setDisplayName("");
        setVendorId("");
        setError(null);
        setOpen(false);
    };

    const canSave =
        !saving
        && displayName.trim().length > 0
        && VENDOR_KEY_PATTERN.test(vendorId.trim());

    const handleSave = async () => {
        if (!canSave) {
            setError(t("integrations_add_vendor_invalid"));
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const res = await apiFetch(
                `/admin/integrations/${encodeURIComponent(channelCode)}/vendors`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        displayName: displayName.trim(),
                        vendorId: vendorId.trim(),
                    }),
                },
            );
            const json = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(json?.message ?? `HTTP ${res.status}`);
            }
            if (json?.channel) onCreated(json.channel);
            reset();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSaving(false);
        }
    };

    if (!open) {
        return (
            <div className="admin-integrations-add-vendor-button-row">
                <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => setOpen(true)}
                    disabled={disabled}
                >
                    <Plus size={14} /> {t("integrations_add_vendor_button")}
                </button>
            </div>
        );
    }

    return (
        <form
            className="admin-integrations-add-vendor-form"
            autoComplete="off"
            onSubmit={(e) => { e.preventDefault(); handleSave(); }}
        >
            <div className="admin-integrations-add-vendor-fields">
                <div className="admin-integrations-field">
                    <label className="admin-integrations-field-label">
                        {t("integrations_add_vendor_display_name")}
                    </label>
                    <input
                        type="text"
                        className="admin-integrations-input"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        placeholder={t("integrations_add_vendor_display_name_placeholder")}
                        maxLength={200}
                        autoFocus
                        disabled={saving}
                    />
                </div>
                <div className="admin-integrations-field">
                    <label className="admin-integrations-field-label">
                        {t("integrations_add_vendor_vendor_id")}
                    </label>
                    <p className="admin-integrations-field-hint">
                        {t("integrations_add_vendor_vendor_id_hint")}
                    </p>
                    <input
                        type="text"
                        className="admin-integrations-input admin-integrations-input--mono"
                        value={vendorId}
                        onChange={(e) => setVendorId(e.target.value.toLowerCase())}
                        placeholder={t("integrations_add_vendor_vendor_id_placeholder")}
                        maxLength={128}
                        disabled={saving}
                    />
                </div>
            </div>
            <div className="admin-integrations-add-vendor-actions">
                <button
                    type="submit"
                    className="btn btn-sm btn-primary"
                    disabled={!canSave}
                >
                    {saving ? (
                        <Loader2 size={14} className="admin-integrations-spinner" />
                    ) : (
                        <Check size={14} />
                    )}{" "}
                    {t("integrations_add_vendor_save")}
                </button>
                <button
                    type="button"
                    className="btn btn-sm"
                    onClick={reset}
                    disabled={saving}
                >
                    <X size={14} /> {t("cancel")}
                </button>
            </div>
            {error && <p className="admin-integrations-field-error">{error}</p>}
        </form>
    );
};

export default AddVendorForm;
